import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchUtils from "../utils/SearchUtils";

const normalizeScanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toUpperCase();
};

const getOrderStockCode = (order) => {
  if (!order) return "";
  return String(order.stock_code || order.stockCode || "").trim();
};

const AddToOrderDialog = ({
  open,
  onClose,
  readyOrders = [],
  selectedOrderId,
  onSelectOrder,
  onAdd,
  onAddAndSave,
  loading = false,
  identifyByStockCode = false,
  enableTrim = false,
  trimChars = SearchUtils.defaultTrimChars,
  t,
}) => {
  const [submittingAction, setSubmittingAction] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [scanError, setScanError] = useState("");
  const scanInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setScanValue("");
      setScanError("");
      setSubmittingAction(null);
      return;
    }
    if (identifyByStockCode) {
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 100);
    }
  }, [open, identifyByStockCode]);

  const selectedOrder = readyOrders.find(
    (order) => order && String(order.id) === String(selectedOrderId)
  );

  const matchOrderByStockCode = (rawValue) => {
    const trimmedValue = enableTrim
      ? SearchUtils.trimLeadingTrailing(rawValue, trimChars)
      : (rawValue == null ? "" : String(rawValue).trim());
    const normalizedInput = normalizeScanValue(trimmedValue);

    if (!normalizedInput) {
      setScanError("");
      onSelectOrder(null);
      return;
    }

    const matched = readyOrders.find((order) => {
      const stockCode = normalizeScanValue(getOrderStockCode(order));
      return stockCode && stockCode === normalizedInput;
    });

    if (matched) {
      setScanError("");
      onSelectOrder(matched.id);
    } else {
      setScanError(
        t("Console.AddToOrder.StockCodeNotFound", {
          defaultValue: "No matching ready order for this stock code.",
        })
      );
      onSelectOrder(null);
    }
  };

  const handleScanChange = (event) => {
    const nextValue = event.target.value;
    setScanValue(nextValue);
    matchOrderByStockCode(nextValue);
  };

  const handleScanKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    matchOrderByStockCode(scanValue);
  };

  const handleAdd = async () => {
    if (!selectedOrderId || loading) return;
    setSubmittingAction("add");
    try {
      await onAdd(selectedOrderId);
    } catch (_) {
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleAddAndSave = async () => {
    if (!selectedOrderId || loading) return;
    setSubmittingAction("addAndSave");
    try {
      await onAddAndSave(selectedOrderId);
    } catch (_) {
    } finally {
      setSubmittingAction(null);
    }
  };

  const getOrderLabel = (order) => {
    if (!order) return "";
    const storageLocation =
      (order.storage_location && String(order.storage_location).trim()) ||
      "";
    const pucode = order.pucode || order.pickup_code || "";
    if (storageLocation && pucode) {
      return `${storageLocation} (${pucode})`;
    }
    if (storageLocation) return storageLocation;
    return pucode || "";
  };

  const isSubmitting = submittingAction !== null || loading;
  const canSubmit = Boolean(selectedOrderId) && !isSubmitting;
  const matchedStockCode = getOrderStockCode(selectedOrder);
  const matchedRoomNumber =
    selectedOrder && selectedOrder.room_number
      ? String(selectedOrder.room_number).trim()
      : "";

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="add-to-order-dialog-title"
        sx={{
          m: 0,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "1.15rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("Console.AddToOrder.Title", { defaultValue: "Add to existing order" })}
        <IconButton
          aria-label={t("Global.Close") || "Close"}
          onClick={onClose}
          sx={{
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.18)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          backgroundColor: "#ffffff",
        }}
      >
        {identifyByStockCode ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#64748b", mb: 2 }}>
              {t("Console.AddToOrder.ScanStockCode", {
                defaultValue: "Scan the stock code of the order to add the prescription to:",
              })}
            </Typography>
            <TextField
              inputRef={scanInputRef}
              value={scanValue}
              onChange={handleScanChange}
              onKeyDown={handleScanKeyDown}
              fullWidth
              autoComplete="off"
              error={Boolean(scanError)}
              helperText={
                scanError ||
                (selectedOrderId
                  ? t("Console.AddToOrder.StockCodeMatched", {
                      defaultValue: "Stock code verified.",
                    })
                  : "")
              }
              placeholder={t("Console.AddToOrder.ScanStockPlaceholder", {
                defaultValue: "Scan or enter stock code",
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {selectedOrderId ? (
                      <CheckCircleIcon sx={{ color: "#22c55e" }} />
                    ) : (
                      <QrCodeScannerIcon sx={{ color: scanError ? "#f87171" : "#7c3aed" }} />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: scanError ? "#f87171" : selectedOrderId ? "#22c55e" : "#cbd5e1",
                    borderWidth: 2,
                  },
                },
              }}
            />
            {selectedOrder && (
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: "1px solid #bbf7d0",
                  backgroundColor: "rgba(34, 197, 94, 0.08)",
                }}
              >
                <Typography sx={{ fontWeight: 600, color: "#166534" }}>
                  {t("Assign.StockCode", { defaultValue: "Stock Code" })}: {matchedStockCode}
                </Typography>
                {matchedRoomNumber ? (
                  <Typography sx={{ color: "#166534", mt: 0.5 }}>
                    {t("Assign.RoomNumber", { defaultValue: "Room number" })}: {matchedRoomNumber}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#64748b", mb: 2 }}>
              {t("Console.AddToOrder.SelectBag", { defaultValue: "Select the bag/order to add the prescription to:" })}
            </Typography>
            <RadioGroup
              value={selectedOrderId != null ? String(selectedOrderId) : ""}
              onChange={(e) => onSelectOrder(e.target.value)}
              sx={{ gap: 0.5 }}
            >
              {readyOrders.map((order) => {
                if (!order || !order.id) return null;
                const label = getOrderLabel(order);
                return (
                  <FormControlLabel
                    key={order.id}
                    value={String(order.id)}
                    control={<Radio size="small" />}
                    label={label}
                    sx={{
                      m: 0,
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleAdd}
          disabled={!canSubmit}
          variant="contained"
          startIcon={
            submittingAction === "add" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{
            minWidth: "120px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
            },
          }}
        >
          {t("Console.AddToOrder.Add", { defaultValue: "Add" })}
        </Button>
        <Button
          onClick={handleAddAndSave}
          disabled={!canSubmit}
          variant="outlined"
          startIcon={
            submittingAction === "addAndSave" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{
            minWidth: "140px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            borderColor: "#7c3aed",
            color: "#7c3aed",
            "&:hover": {
              borderColor: "#6d28d9",
              backgroundColor: "rgba(124, 58, 237, 0.08)",
            },
          }}
        >
          {t("Console.AddToOrder.AddAndSave", { defaultValue: "Add & Print" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddToOrderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  readyOrders: PropTypes.arrayOf(PropTypes.object),
  selectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectOrder: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddAndSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  identifyByStockCode: PropTypes.bool,
  enableTrim: PropTypes.bool,
  trimChars: PropTypes.string,
  t: PropTypes.func.isRequired,
};

export default AddToOrderDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/AddToOrderDialog.jsx